import { Layouts } from '../../controls/layouts';
import { Checkbox } from '../../controls/checkbox';
import { Buttons } from '../../controls/buttons';
import { Component, OnInit, inject, output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { IdentificationType, Partner, PartnerIdentificationType, PartnerType } from '../models';
import { FormGroup, NgForm, FormsModule } from '@angular/forms';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { PartnerService } from '../services/partner.service';
import { PartnerIdentificationTypeService } from '../services/partner-identification-type.service';
import { UniqueValidatorFn } from 'src/app/controls/directives/unique-validator.directive';
import { Textbox } from '../../controls/textbox';
import { UniqueValidatorDirective } from '../../controls/directives/unique-validator.directive';
import { Selectbox } from '../../controls/selectbox';

@Component({
    selector: 'app-new-partner',
    templateUrl: './new-partner.component.html',
    styleUrls: ['./new-partner.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, Layouts, Textbox, UniqueValidatorDirective, Selectbox, Checkbox, Buttons]
})
export class NewPartnerComponent implements OnInit {
  form = viewChild(NgForm);

  saved = output<Partner>();
  cancelled = output<void>();

  private partnerService = inject(PartnerService);
  private currentUserService = inject(CurrentUserService);
  private partnerIdentificationTypeService = inject(PartnerIdentificationTypeService);
  private loadingPanelService = inject(LoadingPanelService);
  private toastService = inject(ToastService);

  partnerTypes: PartnerType[] = [];
  identificationTypes: IdentificationType[] = [];

  newPartner: Partner = null;

  ngOnInit(): void {
    this.initializeData();
  }

  async initializeData(): Promise<void> {
    const [
      partnerTypes,
      identificationTypes,
    ] = await Promise.all([
      this.partnerService.getPartnerTypes(),
      this.partnerIdentificationTypeService.getIdentificationTypes(true),
    ]);

    this.partnerTypes = this.currentUserService.hasPermission('Partner_Action_InternalPartner')
      ? partnerTypes
      : partnerTypes.filter(e => e.Name !== 'Internal');

    this.identificationTypes = identificationTypes;

    this.newPartner = new Partner();
    this.newPartner.PartnerType = this.partnerTypes.length ? this.partnerTypes[0] : { Id: undefined, Name: '' } as PartnerType;
    this.newPartner.MainIdentificationType = new PartnerIdentificationType();
    this.newPartner.MainIdentificationType.IdentificationType = this.identificationTypes.length ? this.identificationTypes[0] : new IdentificationType();
    this.newPartner.MainIdentificationType.IsMainIdentification = true;
    this.newPartner.MainIdentificationType.Value = '';
    this.newPartner.IsProxyPartner = false;
  }

  isPartnerNameUnique: UniqueValidatorFn = async partnerName =>
    partnerName?.length ? await this.partnerService.isPartnerNameAvailable(partnerName) : true;

  async createPartnerByForm(form: FormGroup): Promise<void> {
    try {
      this.loadingPanelService.showLoadingPanelDirect('Creating partner...');

      if (await this.partnerService.isPartnerNameAvailable(this.newPartner.PartnerName)) {
        this.newPartner.PartnerName = form.value.partnerName.trim();
        this.newPartner.PartnerType = this.partnerTypes.find(e => e.Id === Number(form.value.partnerType));
        this.newPartner.MainIdentificationType.IdentificationType = this.identificationTypes.find(e => e.IdentificationTypeId === Number(form.value.identificationType));
        this.newPartner.MainIdentificationType.Value = form.value.identificationValue.trim();
        this.newPartner.IsProxyPartner = form.value.proxyPartner;

        this.newPartner.EdiConfig.Qualifier = this.newPartner.MainIdentificationType.IdentificationType.DefaultQualifierEdifact;
        this.newPartner.EdiConfig.PartnerIdentificationType = this.newPartner.MainIdentificationType;
        this.newPartner.X12Config.Qualifier = this.newPartner.MainIdentificationType.IdentificationType.DefaultQualifierX12;
        this.newPartner.X12Config.PartnerIdentificationType = this.newPartner.MainIdentificationType;

        const savedPartner = await this.partnerService.savePartner(this.newPartner);
        this.saved.emit(savedPartner);
      }
      else {
        this.toastService.showToast('Partner name is already used!', ToastType.error);
      }
    }
    catch (error) {
      this.toastService.showToast('Failed to create partner!', ToastType.error);
    }
    finally {
      this.loadingPanelService.hideLoadingPanel();
    }
  }

  submit(ngForm: NgForm): void {
    this.createPartnerByForm(ngForm.form);
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
